import React, { useState } from 'react';
import Editor from "@monaco-editor/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/shadcn/card.tsx";
import { Button } from "@/components/shadcn/button.tsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/shadcn/tabs.tsx";
import { Icon } from "@/components/Icon.tsx";

const Playground = () => {
  const [activeTab, setActiveTab] = useState("custom");
  const [customCode, setCustomCode] = useState(`// Welcome to the Playground!
// Write your browser automation code here

import { Browser, Page } from 'puppeteer';

async function runScript() {
  try {
    const browser = await puppeteer.launch({
      headless: false,
      args: ['--no-sandbox', '--disable-setuid-sandbox']
    });
    
    const page = await browser.newPage();
    await page.goto('https://example.com');
    
    // Your automation code here
    const title = await page.title();
    console.log('Page title:', title);
    
    await browser.close();
  } catch (error) {
    console.error('Error:', error);
  }
}

runScript();`);

  const [httpApiCode, setHttpApiCode] = useState(`curl -X POST "https://api.browsercloud.com/screenshot" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://example.com",
    "options": {
      "width": 1280,
      "height": 720,
      "format": "png",
      "fullPage": false
    }
  }'`);

  const [isLoading, setIsLoading] = useState(false);

  const handleRunCode = async () => {
    setIsLoading(true);
    // TODO: Implement code execution logic
    const currentCode = activeTab === "custom" ? customCode : httpApiCode;
    console.log('Running code:', currentCode);
    
    // Simulate execution delay
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  const handleCustomCodeChange = (value: string | undefined) => {
    if (value !== undefined) {
      setCustomCode(value);
    }
  };

  const handleHttpApiCodeChange = (value: string | undefined) => {
    if (value !== undefined) {
      setHttpApiCode(value);
    }
  };

  return (
    <div className="w-full h-full min-h-screen flex flex-col">
      {/* Header */}
      <Card className="rounded-none border-b">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Browser Automation Playground</CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={handleRunCode}
                disabled={isLoading}
                className="[&_svg]:size-4"
              >
                {isLoading ? (
                  <>
                    <Icon icon="fluent:spinner-ios-20-regular" className="animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Icon icon="fluent:play-24-regular" />
                    Run Code
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Split Layout */}
      <div className="flex flex-1 h-full">
        {/* Left Side - Code Editor with Tabs */}
        <div className="w-1/2 border-r h-full">
          <Card className="h-full rounded-none border-0">
            <CardContent className="p-0 h-full">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
                <div className="border-b px-4 py-2">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="http">HTTP API</TabsTrigger>
                    <TabsTrigger value="custom">Run Custom Code</TabsTrigger>
                  </TabsList>
                </div>
                
                <TabsContent value="http" className="h-[calc(100vh-180px)] m-0">

                </TabsContent>
                
                <TabsContent value="custom" className="h-[calc(100vh-180px)] m-0">
                  <Editor
                    height="100%"
                    defaultLanguage="javascript"
                    value={customCode}
                    onChange={handleCustomCodeChange}
                    theme="vs-dark"
                    options={{
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      fontSize: 14,
                      lineNumbers: 'on',
                      renderLineHighlight: 'line',
                      automaticLayout: true,
                      wordWrap: 'on',
                      folding: true,
                      bracketPairColorization: { enabled: true },
                      suggest: {
                        enabled: true
                      },
                      quickSuggestions: {
                        other: true,
                        comments: true,
                        strings: true
                      }
                    }}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Right Side - Browser Preview */}
        <div className="w-1/2">
          <Card className="h-full rounded-none border-0">
            <CardHeader className="py-2 px-4 border-b">
              <CardTitle className="text-sm text-muted-foreground">Browser Live Preview</CardTitle>
            </CardHeader>
            <CardContent className="p-0 h-full">
              <div className="h-[calc(100vh-140px)] flex items-center justify-center bg-muted/20">
                {isLoading ? (
                  <div className="flex flex-col items-center gap-4">
                    <Icon icon="fluent:spinner-ios-20-regular" className="animate-spin h-8 w-8" />
                    <p className="text-muted-foreground">Executing browser automation...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-4 text-center">
                    <Icon icon="fluent:window-dev-tools-16-regular" className="h-16 w-16 text-muted-foreground" />
                    <div>
                      <p className="text-lg font-medium">Browser Preview</p>
                      <p className="text-sm text-muted-foreground">
                        Click "Run Code" to see your browser automation in action
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Playground;